import { Chip, Tooltip } from "@mui/material";
import type { BlotterColumn } from "@/components/Blotter";
import GeneratedAvatar from "@/components/users/GeneratedAvatar";
import RoleChip from "@/components/users/RoleChip";
import { UserRowMenu } from "@/components/usersOrgs/UserRowMenu";
import type { User, UserActionToast } from "@/interfaces/user";
import {
  formatLastSignIn,
  formatRegistered,
  hasPossibleRowActions,
  type UserRowActionPerms,
} from "@/utils/userRowActions-helper";

function statusOf(u: User): "Enabled" | "Invited" | "Archived" {
  if (u.archived) return "Archived";
  if (u.verified) return "Enabled";
  return u.clerkInvitationId ? "Invited" : "Enabled";
}

function titleCase(s: string | null): string {
  if (!s) return "—";
  return s.charAt(0).toUpperCase() + s.slice(1);
}

export interface UsersColumnsDeps {
  perms: UserRowActionPerms;
  onEdit: (u: User) => void;
  onToast: (t: UserActionToast) => void;
  onChangeEmail: (u: User) => void;
}

export function usersBlotterColumns(deps: UsersColumnsDeps): BlotterColumn<User>[] {
  return [
    {
      key: "name",
      label: "Name",
      sortable: true,
      sortValue: (u) => u.name,
      render: (u) => (
        <span style={{ display: "inline-flex", alignItems: "center", gap: 10 }}>
          <GeneratedAvatar id={u.id} size={28} alt={u.name} />
          <button
            type="button"
            onClick={() => deps.onEdit(u)}
            style={{
              border: "none",
              background: "none",
              padding: 0,
              cursor: "pointer",
              fontWeight: 600,
              fontFamily: "var(--font-lato)",
              fontSize: 14,
              // Demo users read muted; the chip beside the name is the accessible signal.
              color: u.organizationIsFake ? "var(--color-graphite)" : "var(--color-rodeo-black)",
              textAlign: "left",
            }}
          >
            {u.name}
          </button>
          {u.organizationIsFake && (
            <Tooltip title="Demo organization user — seeded from an anonymized bundle (Users & Orgs → Demo).">
              <Chip
                size="small"
                variant="outlined"
                label="Demo"
                sx={{
                  height: 20,
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: "0.5px",
                  color: "var(--color-graphite)",
                  borderColor: "var(--color-smoke)",
                }}
              />
            </Tooltip>
          )}
        </span>
      ),
    },
    {
      key: "email",
      label: "Email",
      sortable: true,
      sortValue: (u) => u.email,
      render: (u) => <span style={{ color: "var(--color-graphite)" }}>{u.email}</span>,
    },
    {
      key: "organization",
      label: "Organization",
      sortable: true,
      sortValue: (u) => u.organization ?? "",
      render: (u) => u.organization ?? "—",
    },
    {
      key: "plan",
      label: "Plan",
      sortable: true,
      sortValue: (u) => u.plan ?? "",
      render: (u) => titleCase(u.plan),
    },
    {
      key: "orgRole",
      label: "Org Role",
      sortable: true,
      sortValue: (u) => u.orgRole ?? "",
      render: (u) =>
        u.orgRole ? (
          <RoleChip role={u.orgRole} />
        ) : (
          <span style={{ color: "var(--color-graphite)" }}>None</span>
        ),
    },
    {
      // GFG role is only meaningful for GFG staff — surface the column only when the
      // "GFG User" filter is active (so only GFG viewers, viewing GFG users, see it).
      key: "gfgRole",
      label: "GFG Role",
      visibleWhen: (filters) => filters.some((f) => f.fieldId === "gfg"),
      render: (u) =>
        u.gfgRole ? (
          <RoleChip role={u.gfgRole} />
        ) : (
          <span style={{ color: "var(--color-graphite)" }}>—</span>
        ),
    },
    {
      key: "lastSignIn",
      label: "Last Sign In",
      sortable: true,
      sortValue: (u) => u.lastSignInAt ?? "",
      exportValue: (u) => formatLastSignIn(u.lastSignInAt),
      render: (u) =>
        u.lastSignInAt === null ? (
          <span style={{ color: "var(--color-graphite)" }}>{formatLastSignIn(null)}</span>
        ) : (
          formatLastSignIn(u.lastSignInAt)
        ),
    },
    {
      key: "registered",
      label: "Registered",
      sortable: true,
      sortValue: (u) => u.registeredAt ?? "",
      exportValue: (u) => formatRegistered(u.registeredAt),
      render: (u) => formatRegistered(u.registeredAt),
    },
    {
      key: "status",
      label: "Status",
      sortable: true,
      sortValue: (u) => statusOf(u),
      render: (u) => statusOf(u),
    },
    {
      key: "actions",
      label: "",
      exportable: false,
      render: (u) =>
        hasPossibleRowActions(u, deps.perms) ? (
          <UserRowMenu
            user={u}
            perms={deps.perms}
            onToast={deps.onToast}
            onChangeEmail={deps.onChangeEmail}
          />
        ) : null,
    },
  ];
}
